/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  formatNumber,
  Icon,
  Skeleton,
  Tooltip,
  Typography,
} from '@omnipresentgroup/design-system';
import type { InvoiceReportEmployee } from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

export enum InvoiceReportEmployeesColumnHeaders {
  fullName = 'Employee',
  country = 'Country',
  role = 'Role',
  notes = 'Notes',
  totalPreviousInvoice = 'Total previous invoice',
  totalCurrentInvoice = 'Total this invoice',
}

export const useInvoiceReportEmployeesTableConfig = () => {
  const columnHelper = useMemo(
    () => createColumnHelper<InvoiceReportEmployee>(),
    [],
  );

  const columns: ColumnDef<InvoiceReportEmployee, any>[] = useMemo(
    () => [
      columnHelper.accessor('fullName', {
        id: 'employee',
        cell: (info) => info.getValue() ?? '',
        header: () => InvoiceReportEmployeesColumnHeaders.fullName,
      }),
      columnHelper.display({
        id: 'country',
        cell: (info) => info.row.original.country.name,
        header: () => InvoiceReportEmployeesColumnHeaders.country,
      }),
      columnHelper.accessor('role', {
        cell: (info) => info.getValue() ?? '-',
        header: () => InvoiceReportEmployeesColumnHeaders.role,
      }),
      columnHelper.accessor('billingNotes', {
        cell: (info) =>
          !!info.getValue() && (
            <Tooltip
              description={info.getValue()}
              id="invoice-report-employee-notes"
            >
              <Icon icon="BrandLine" size="16" />
            </Tooltip>
          ),
        header: () => InvoiceReportEmployeesColumnHeaders.notes,
        meta: {
          loader: <Skeleton width={16} height={16} circle />,
        },
      }),
      columnHelper.display({
        id: 'totalPrevInvoice',
        cell: (info) =>
          info.row.original.totalPrevInvoice
            ? formatNumber({
                input: Number(info.row.original.totalPrevInvoice.amount),
                currency: info.row.original.totalPrevInvoice.currency,
              })
            : '-',
        header: () => InvoiceReportEmployeesColumnHeaders.totalPreviousInvoice,
      }),
      columnHelper.display({
        id: 'totalCurrInvoice',
        cell: (info) =>
          info.row.original.billedAmount ? (
            <Typography as="span" size="14" weight="medium">
              {formatNumber({
                input: Number(info.row.original.billedAmount.amount),
                currency: info.row.original.billedAmount.currency,
              })}
            </Typography>
          ) : (
            '-'
          ),
        header: () => InvoiceReportEmployeesColumnHeaders.totalCurrentInvoice,
      }),
    ],
    [columnHelper],
  );

  return {
    columns,
    columnHelper,
  };
};
